import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StoreState } from '../../store'

import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles } from '@material-ui/core/styles'
import GridTextField from '../../components/GridTextField'
import { Grid } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'

import SaveAltIcon from '@material-ui/icons/SaveAlt'
import DeleteIcon from '@material-ui/icons/Delete'

import moment from 'moment'

import HeaderDetails from '../../components/HeaderDetails'
import { PatrimonyActionTypes } from 'store/patrimony/patrimonyTypes'
import {
  removePatrimony,
  savePatrimony,
} from 'store/patrimony/patrimonyActions'
import { useForm } from 'react-hook-form'

import { applyMaskOnChange, setMask } from '../../utils'

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    height: '100%',
    overflowY: 'scroll',
    padding: theme.spacing(2),
  },
  input: {
    margin: theme.spacing(1),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
}))

interface Form {
  value: string
  description: string
  date: string
  serial_number: string
  category: string
  area: string
  object: string
  model: string
  responsibly: string
  location: string
  conservation_state: string
  status: string
  nf: string
}

const emptyForm: Form = {
  nf: '',
  area: '',
  value: '',
  model: '',
  status: '',
  object: '',
  location: '',
  category: '',
  description: '',
  responsibly: '',
  serial_number: '',
  conservation_state: '',
  date: moment().format('YYYY-MM-DD'),
}

export const PatrimonyDetails = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { isEditing, originalData, savingPatrimony, removingPatrimony } =
    useSelector(({ patrimonyState }: StoreState) => ({
      savingPatrimony: patrimonyState.savingPatrimony,
      removingPatrimony: patrimonyState.removingPatrimony,
      isEditing: patrimonyState.selectedIndex !== null,
      filter: patrimonyState.filterPatrimony,
      originalData:
        patrimonyState.selectedIndex !== null
          ? patrimonyState.patrimony[patrimonyState.selectedIndex]
          : null,
    }))

  const [isOpenModalRemove, setOpenModalRemove] = useState(false)
  const openModalRemove = () => setOpenModalRemove(true)
  const closeModalRemove = () => setOpenModalRemove(false)

  useEffect(() => {
    console.table(originalData)
  }, [originalData])

  const formValues = !originalData
    ? emptyForm
    : {
        date: originalData.acquisition_date.slice(0, 10),
        value: setMask('real', originalData.acquisition_value),
        location: originalData.location,
        description: originalData.description,
        serial_number: originalData.serial_number,
        responsibly: originalData.responsibly,
        model: originalData.model,
        status: originalData.status,
        nf: originalData.nf,
        conservation_state: originalData.conservation_state,
        category: originalData.category,
        area: originalData.area,
        object: originalData.object,
      }

  const form = useForm({
    defaultValues: !originalData ? emptyForm : formValues,
  })

  // useEffect(() => {
  //   form.register({ name: 'value' })
  //   form.register({ name: 'description' })
  //   form.register({ name: 'location' })
  // }, [form])

  const value = form.watch('value')
  const description = form.watch('description')
  const location = form.watch('location')

  const onClose = () => {
    dispatch({ type: PatrimonyActionTypes.EDITING_CANCEL })
  }

  const onSubmitForm = useCallback(async () => {
    await form.triggerValidation()
    const data = form.getValues()
    if (!value) {
      form.setError('value', '', 'Preencha este campo')
    }
    if (!description) {
      form.setError('description', '', 'Preencha este campo')
    }
    if (!location) {
      form.setError('location', '', 'Preencha este campo')
    }
    if (Object.entries(form.errors).length) {
      return
    }

    dispatch(savePatrimony(data))
    form.reset(emptyForm)
  }, [dispatch, form, location, description, value])

  const onClickConfirmRemovePatrimony = () => {
    closeModalRemove()
    dispatch(removePatrimony())
  }

  return (
    <>
      <Box className={classes.container}>
        <HeaderDetails
          title={isEditing ? 'Editando cadastro' : 'Novo cadastro'}
          onClose={onClose}
        />
        <FormControl component="fieldset" className={classes.content}>
          <Grid container spacing={3}>
            <GridTextField
              gridSize={6}
              label="Nome *"
              name="description"
              placeholder="Nome do bem, item"
              inputRef={form.register}
              errorMessage={form.errors?.description?.message}
            />

            <GridTextField
              gridSize={6}
              label="Numero de serie"
              placeholder="Nº Serie ou registo"
              name="serial_number"
              inputRef={form.register}
            />

            <GridTextField
              label="Valor *"
              name="value"
              placeholder="R$ 0,00"
              gridSize={6}
              inputRef={form.register}
              errorMessage={form.errors?.value?.message}
              onChange={applyMaskOnChange('real')}
            />
            <GridTextField
              label="Data de aquisição"
              name="date"
              type="date"
              gridSize={6}
              inputRef={form.register({
                required: 'Preencha a data',
              })}
            />

            <GridTextField
              label="Localização"
              name="location"
              type="text"
              placeholder="Onde o item se encontra atualmente"
              gridSize={6}
              inputRef={form.register({
                required: 'Preencha localização',
              })}
              errorMessage={form.errors?.location?.message}
            />

            <GridTextField
              label="Modelo"
              name="model"
              type="text"
              placeholder="Exemplo, Carro BMW"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Grupo"
              name="category"
              type="text"
              placeholder="Exemplo Carro, Moveis"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Responsável"
              name="responsibly"
              placeholder="Pessoa responsável pelo bem"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Status"
              name="status"
              placeholder="Exemplo, Uso, Manutenção, Estoque"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Estado de conservação"
              name="conservation_state"
              placeholder="Exemplo, Ótimo, Bom, Defeito"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Objeto"
              name="object"
              placeholder="Exemplo, Mesa, Computador"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Area"
              name="area"
              placeholder="Área ou zona qual o item pertence"
              gridSize={6}
              inputRef={form.register}
            />

            <GridTextField
              label="Numero NF"
              name="nf"
              placeholder="Numero da nota fiscal"
              gridSize={8}
              inputRef={form.register}
            />
          </Grid>
        </FormControl>

        <Box>
          <Divider />
          <Box m={2}>
            <Button
              onClick={onSubmitForm}
              size="large"
              color="primary"
              variant="contained"
              disabled={savingPatrimony}
              className={classes.marginRight}
            >
              <SaveAltIcon className={classes.marginRight} />
              {savingPatrimony ? 'AGUARDE...' : 'SALVAR'}
            </Button>
            <Button
              onClick={openModalRemove}
              size="large"
              disabled={!isEditing}
              className={classes.marginRight}
            >
              <DeleteIcon className={classes.marginRight} />
              {/* {removingTransfer ? 'AGUARDE...' : 'EXCLUIR'} */}
              EXCLUIR
            </Button>
          </Box>
        </Box>

        <Dialog open={isOpenModalRemove} onClose={closeModalRemove}>
          <DialogTitle>Excluir este cadastro?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Esta ação não poderá ser desfeita.
              <br />
              Tem certeza que deseja remover este cadastro?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={removingPatrimony}
              onClick={closeModalRemove}
              color="primary"
            >
              Não
            </Button>
            <Button
              disabled={removingPatrimony}
              onClick={onClickConfirmRemovePatrimony}
              color="primary"
              autoFocus
            >
              SIM
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  )
}

// "id": "ec0e540a-bcee-4108-b194-9c1f285f7614",
// "createdAt": "2023-06-21T17:34:58.886Z",
// "updatedAt": "2023-06-21T17:34:58.886Z",
// "description": "Descrição do item em si ou o nome",
// "acquisition_value": "2.50",
// "acquisition_date": "2023-06-21T14:34:58.666Z",
// "serial_number": "Nº Serie ou registo",
// "category": "Categoria carro, moveis",
// "object": "Objeto tipo mesa",
// "model": "Modelo",
// "responsibly": "Responsável ou dono do item",
// "area": "Área ou zona qual o item pertence",
// "location": "localização do item",
// "conservation_state": "Estado de conservação esta",
// "status": "Status tipo em uso ou manutenção",
// "nf": "numero nota fiscal quando houver",
// "churchId": "8b4f36a0-df8c-4f00-b7c0-81c386dea5e1",
// "userId": "cd2a4e64-1dd9-47c9-b8a7-72f486ff6c14",
// "depreciations": []
